import { React, useState, useEffect, useRef } from 'libs/react';
import { useDealConfiguratorContext } from '../../../DealConfigureScreen';
import { FormDate } from 'components/form/FormDate';
import { Button } from 'components/Button';
import { getConfigurationFieldValue } from 'helpers/ConfigurationField';
import { DealAvailabilityModal } from 'modules/deal/modal/DealAvailabilityModal';
import './DealPlannedDatePanel.scss';
import { Requires } from 'components/user/Requires';
/**
 * @component DealPlannedDatePanel.tsx
 * @since 1.0.0
 */
export function DealPlannedDatePanel(props) {
    const { deal } = props;
    const { updateDealField, configurationFields } = useDealConfiguratorContext();
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    // Get the mode from configuration
    const plannedDateMode = getConfigurationFieldValue(configurationFields, 'PLANNED_DATE_MODE');
    const mode = plannedDateMode || 'DATE';
    // Build the value from deal server state.
    // Dates are stored as UTC midnight ("YYYY-MM-DD" → new Date → UTC midnight).
    // We extract the UTC date components and produce a local-midnight string (no Z)
    // so the datepicker interprets the correct calendar date regardless of browser timezone.
    const toLocalMidnight = (isoStr) => {
        const d = new Date(isoStr);
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${day}T00:00:00`;
    };
    const getPlannedDateValue = () => {
        if (mode === 'RANGE' && deal.plannedDateStart && deal.plannedDateEnd) {
            return `${toLocalMidnight(deal.plannedDateStart)},${toLocalMidnight(deal.plannedDateEnd)}`;
        }
        if (deal.plannedDateStart) {
            return toLocalMidnight(deal.plannedDateStart);
        }
        return null;
    };
    // Local picker value — decoupled from deal state so opening the picker
    // in RANGE mode doesn't trigger an auto-save with null dates.
    const [localPickerValue, setLocalPickerValue] = useState(getPlannedDateValue());
    const skipNextNullRef = useRef(false);
    // Sync local picker value when server-side deal dates change
    useEffect(() => {
        setLocalPickerValue(getPlannedDateValue());
    }, [deal.plannedDateStart, deal.plannedDateEnd, mode]);
    const handleChange = (value) => {
        // FormDate.useWatch fires onChange(null) when we reset localPickerValue
        // on calendar open — skip only that synthetic null, not an explicit clear.
        if (!value && skipNextNullRef.current) {
            skipNextNullRef.current = false;
            return;
        }
        setLocalPickerValue(value);
        if (!value) {
            updateDealField('plannedDate', '', 'string');
            return;
        }
        if (mode === 'RANGE' && value.includes(',')) {
            const dates = value.split(',');
            if (dates.length === 2) {
                // Pass full ISO strings so backend preserves the correct local date
                updateDealField('plannedDate', `${dates[0]} - ${dates[1]}`, 'string');
            }
        }
        else {
            updateDealField('plannedDate', value, 'string');
        }
    };
    return (React.createElement("div", { className: "deal-planned-date-panel" },
        React.createElement("div", { className: "deal-planned-date-panel-content" },
            React.createElement(FormDate, { name: "plannedDate", label: t('Planned Date'), value: localPickerValue, selectsRange: mode === 'RANGE', time: false, isClearable: true, placeholder: t('To be confirmed'), onChange: handleChange, onCalendarOpen: () => {
                    if (mode === 'RANGE') {
                        skipNextNullRef.current = true;
                        setLocalPickerValue(null);
                    }
                } }),
            React.createElement(Requires, { permission: "calendar.availability.check" },
                React.createElement("div", { className: "deal-availability-tooltip" },
                    React.createElement(Button, { style: "picto", picto: "calendar-check", size: "small", disabled: !deal.plannedDateStart, onPress: () => setShowAvailabilityModal(true) }),
                    React.createElement("span", { className: "deal-availability-tooltip-value" }, t('Check availability'))))),
        showAvailabilityModal && (React.createElement(DealAvailabilityModal, { deal: deal, onClose: () => setShowAvailabilityModal(false) }))));
}
